import { useEffect, useRef } from "react";
import type { JSX, ReactNode } from "react";
import { useLocation } from "react-router";
import { useI18n } from "../i18n/I18nContext";
import { PageBanners } from "./PageBanners";

/** Properties of {@link PageHeader}. */
export interface PageHeaderProps {
  /** Page title. */
  readonly title: string;
  /** Optional actions or hints shown below the title. */
  readonly children?: ReactNode;
}

/**
 * Page heading that also sets the document title and, after a navigation within the app, moves
 * the focus to the heading, so screen reader users hear the new page and keyboard users continue
 * from its top (WCAG 2.4.3). The first page load keeps the browser's focus, and changes of the
 * query string within a page (filters, views) do not move it, nor does an address with a fragment,
 * whose section takes the focus (`useFocusHashTarget`). The banners of the app shell follow right
 * after the header (UX-001, {@link PageBanners}).
 *
 * @param props - Component properties.
 * @returns The header element.
 */
export function PageHeader(props: PageHeaderProps): JSX.Element {
  const { t } = useI18n();
  const appTitle = t("app.title");
  const location = useLocation();
  const headingRef = useRef<HTMLHeadingElement>(null);
  const isInitialEntryRef = useRef(true);
  const namesSectionRef = useRef(false);

  useEffect(() => {
    document.title = `${props.title} – ${appTitle}`;
  }, [props.title, appTitle]);

  // Declared before the focus effect, so that effect reads the entry of the current render.
  useEffect(() => {
    isInitialEntryRef.current = location.key === "default";
    namesSectionRef.current = location.hash !== "";
  });

  useEffect(() => {
    // A fragment (`/privacy#privacy-info`) names the section that receives the focus instead.
    if (!isInitialEntryRef.current && !namesSectionRef.current) {
      headingRef.current?.focus();
    }
  }, [location.pathname]);

  const header = (
    <>
      <header>
        <h1 ref={headingRef} tabIndex={-1}>
          {props.title}
        </h1>
        {props.children}
      </header>
      <PageBanners />
    </>
  );

  return header;
}
